import { Box, Typography } from '@mui/material';
import React, { ReactElement, useState } from 'react';
import type { Organization } from 'services/api';
import type { DateFilterPreset } from 'hooks/useAnalyticsDateFilter';
import { OrganizationLeadersTab } from '../OrganizationLeadersTab';
import { AttendeesSubTab } from './AttendeesSubTab';
import { MemberRosterSubTab } from './MemberRosterSubTab';
import { PeopleSubTabs, PeopleSubTabValue } from './PeopleSubTabs';

export interface PeopleTabProps {
  organizationId: string;
  organizationName: string;
  orgLeaders: Organization['orgLeaders'] | undefined;
  orgAdmins: Organization['admins'] | undefined;
  pendingAdminRequests: Organization['pendingAdminRequests'] | undefined;
  onRefresh: () => Promise<void>;
  onReInvite: (leaderId: string) => Promise<void>;
  onAddLeader: () => void;
  dateFilterPreset: DateFilterPreset;
  dateFilterStartDate: Date | null;
  dateFilterEndDate: Date | null;
  onOpenDateFilter: () => void;
}

export function PeopleTab ({
  organizationId,
  organizationName,
  orgLeaders,
  orgAdmins,
  pendingAdminRequests,
  onRefresh,
  onReInvite,
  onAddLeader,
  dateFilterPreset,
  dateFilterStartDate,
  dateFilterEndDate,
  onOpenDateFilter,
}: PeopleTabProps): ReactElement {
  const [selected, setSelected] = useState<PeopleSubTabValue>('attendees');

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '48px',
        width: '100%',
        mt: '48px',
      }}
    >
      <Typography
        sx={{
          fontFamily: '\'Poppins\', sans-serif',
          fontWeight: 600,
          fontSize: '24px',
          lineHeight: '31px',
          color: '#383838',
        }}
      >
        People
      </Typography>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '32px',
          width: '100%',
        }}
      >
        <PeopleSubTabs selected={selected} onChange={setSelected} />

        {selected === 'attendees' ? (
          <AttendeesSubTab
            organizationId={organizationId}
            organizationName={organizationName}
            dateFilterPreset={dateFilterPreset}
            dateFilterStartDate={dateFilterStartDate}
            dateFilterEndDate={dateFilterEndDate}
            onOpenDateFilter={onOpenDateFilter}
          />
        ) : null}

        {selected === 'members' ? (
          <MemberRosterSubTab organizationId={organizationId} />
        ) : null}

        {selected === 'leadership' ? (
          <OrganizationLeadersTab
            organizationId={organizationId}
            organizationName={organizationName}
            orgLeaders={orgLeaders}
            orgAdmins={orgAdmins}
            pendingAdminRequests={pendingAdminRequests}
            onRefresh={onRefresh}
            onReInvite={onReInvite}
            onAddLeader={onAddLeader}
            dateFilterPreset={dateFilterPreset}
            dateFilterStartDate={dateFilterStartDate}
            dateFilterEndDate={dateFilterEndDate}
            onOpenDateFilter={onOpenDateFilter}
          />
        ) : null}
      </Box>
    </Box>
  );
}
